import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Socket } from 'ngx-socket-io';
import { Subscription, Subject, takeUntil } from 'rxjs';
import { urlRoutes } from 'src/app/core/constants/url-route.constant';
import { PaginationType } from 'src/app/core/interfaces/pagination/pagination-type';
import { ApplicationMasterService } from 'src/app/core/services/applicationMaster/application-master.service';
import { LoaderService } from 'src/app/core/services/loader/loader.service';
import { NoticeBoardService } from 'src/app/core/services/notice-board/notice-board.service';
import { SocketService } from 'src/app/core/services/socket/socket.service';
import { ToasterService } from 'src/app/core/services/toaster/toaster.service';
import { SocketNoticeService } from 'src/app/views/layout/services/socket-notice.service';


@Component({
    selector: 'app-notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.scss'],
    imports:[CommonModule, MatButtonModule, MatMenuModule, MatBadgeModule],
    standalone: true,
    providers:[SocketService, SocketNoticeService]
})
export class NotificationsComponent {
    notifications: any[] = [];
    allNotifications: any[] = [];
    count: number = 0;
    private subscription: Subscription;
    private _unsubscribeAll: Subject<any> = new Subject();

    pagination_config: PaginationType = {
        size: 10,
        total: 0,
        skip: 0,
        limit: 10,
    };

    constructor(
        private _notificationService: NoticeBoardService,
        private _toasterService: ToasterService,
        private _socketService: SocketService,
        private translate: TranslateService,
        private _loaderService: LoaderService,
        private router: Router,
        private _appMasterService: ApplicationMasterService
    ) {}

    ngOnInit(): void {
        this.getInitialData();
        let userId =
            JSON.parse(localStorage.getItem('user_details') || '')?.id || '';

            this._socketService
            .customSocketOn('connect')
            .subscribe((data: any) => {
                this._socketService.emitToChannel('application-notification', {
                    userId: userId,
                    userType: 'admin',
                });
            });

        // Start the interval for subsequent calls
        this._socketService
            .listenToChannel('application-notification')
            .subscribe((data: any) => {
                if (
                    data &&
                    data?.sourceArray
                        .map((item) => item?.userId)
                        .includes(userId) &&
                    data?.userType === 'admin'
                ) {
                    this.allNotifications.push(data);
                    this.count = this.allNotifications.filter(
                        (item) => item?.isRead === 'false'
                    )?.length;
                    this.notifications = this.formatData(this.allNotifications);
                }
            });
    }

    getInitialData() {
        this._notificationService
            .getNotification({}, 'false')
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (res: any) => {
                    if (
                        res?.statusCode === 200 &&
                        res?.success &&
                        res?.data?.data.length > 0
                    ) {
                        this.allNotifications = res?.data?.data;
                        this.count = this.allNotifications.filter(
                            (item) => item?.isRead === 'false'
                        )?.length;
                        this.notifications = this.formatData(res?.data?.data);
                    }
                },
            });
    }

    removeNotification(i: number) {
        const data = this.notifications.splice(i, 1);
        const index = this.allNotifications.findIndex(
            (item) => item?.id === data[0]?.id
        );
        if (index !== -1) {
            this.allNotifications.splice(index, 1);
        }
    }

    formatData(dataArr: any = []) {
        return dataArr
            .filter((item) => item?.isRead === 'false')
            .sort((a, b) => b?.commentData?.date - a?.commentData?.date)
            .slice(0, 5)
            .map((item) => ({
                id: item?.id,
                title: item?.title,
                time: this._notificationService.getTimeDifference(
                    item?.commentData?.date
                ),
                referenceId: item?.referenceId,
                isRead: item?.isRead === 'true',
                type: item?.commentData ? item?.commentData?.type : '',
            }));
    }

    markAsRead(
        referenceId: string,
        status: boolean = false,
        isRedirect: boolean = false,
        type: string = '',
        refId: string = ''
    ) {
        this._notificationService
            .updateNotification(referenceId, status)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: () => {
                    if (!isRedirect) {
                        this._toasterService.showSuccess(
                            this.translate.instant('SUCCESS_TOASTER'),
                            this.translate.instant('SUCCESS_STATUS_UPDATE')
                        );
                    }
                    this.getInitialData();
                    if (isRedirect) {
                        this.searchApplicant(refId, type);
                    }
                },
                error: (error: HttpErrorResponse) => {
                    console.log(error);
                },
            });
    }

    routeTo(referenceId: string, type: string) {
        this.router.navigate(
            ['/agent/applications-history/view-application', referenceId],
            {
                queryParams: { type },
            }
        );
    }

    updateData() {
        this.notifications = this.formatData(this.allNotifications);
    }

    searchApplicant(event: string = '', type: string) {
        this._loaderService.showLoader();
        this._appMasterService
            .globalSearch(this.pagination_config, event)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(
                (result) => {
                    if (result) {
                        this._loaderService.hideLoader();
                        result = result?.data?.data[0];
                        this.router.navigate(
                            [
                                `${urlRoutes.mainLayout}/${urlRoutes.application_master}/${urlRoutes.app_master_info}/${result.id}/${result.masterId}/${result?.coursesEnrolementRef?.universityId}`,
                            ],
                            {
                                queryParams: { type },
                            }
                        );
                    }
                },
                (err) => {
                    this._loaderService.hideLoader();
                }
            );
    }

    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }
}
